import React, { useState } from "react";
import { auth, db } from "./firebaseConfig";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { Lock, Mail, Loader2, ExternalLink, Info, ShieldCheck, AlertTriangle } from "lucide-react";
import loginBg from "./logincover.jpg";
import logo from './logo.png';

const AdminLogin = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // 🆕 Added error state

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(""); // Clear error on new attempt

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const emailLower = user.email.toLowerCase().trim();

      const adminDoc = await getDoc(doc(db, "admins", emailLower));
      
      if (adminDoc.exists()) {
        onLoginSuccess();
      } else {
        setError(`Access Denied: ${emailLower} is not authorized. Please contact the lead administrator.`);
        await auth.signOut();
      }
    } catch (err) {
      console.error("Auth Error:", err.code);
      setError("Access Denied: Invalid credentials. Please contact the administrator for assistance.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page-wrapper">
      {/* 🏛️ HEADER */}
      {/* 🏛️ UPDATED HEADER */}
<header className="login-top-nav">
  <div className="nav-brand-area">
    <img src={logo} alt="SafeGuard" className="master-logo" />
  </div>
  <div className="nav-tagline-center">OFFICIAL ADMINISTRATION INTERFACE</div>
  <div className="nav-spacer">
    {/* Public link for Google Play Compliance */}
    <a href="/privacy" style={{ 
      color: '#fbbf24', 
      fontSize: '11px', 
      textDecoration: 'none', 
      border: '1px solid #fbbf24', 
      padding: '5px 10px', 
      borderRadius: '4px',
      fontWeight: 'bold'
    }}>
      PRIVACY POLICY
    </a>
  </div>
</header>

      <div className="login-grid">
        {/* --- LEFT SECTION: BEAUTIFIED TEXT --- */}
        <div className="grid-left">
          <div className="animated-bg" style={{ backgroundImage: `url(${loginBg})` }}></div>
          <div className="overlay-content">
            <div className="badge">
               <ShieldCheck size={14} /> OFFICIAL PORTAL
            </div>
            <h1 className="hero-text">
              Welcome to <br/>
              <span className="gold-text">SafeGuard NG</span> <br/>
              <span className="light-text">Admin Portal</span>
            </h1>
            <p className="description">
              This specialized interface is <span className="highlight-white">strictly reserved</span> for authorized government agencies and law enforcement personnel. <span className="highlight-gold">Access is monitored and logged.</span>
            </p>
            
            <div className="support-box">
              <Info size={20} className="info-icon-gold" />
              <div>
                <p className="support-label">For technical assistance:</p>
                <a href="mailto:support@nathconsult.com" className="support-link">support@nathconsult.com</a>
              </div>
            </div>
          </div>
        </div>

        {/* --- RIGHT SECTION: SCROLLABLE FORM --- */}
        <div className="grid-right">
          <div className="form-scroll-container">
            <div className="form-content-box">
              <header className="form-header">
                <div className="form-logo-box">
                   <img src={logo} alt="Logo" style={{ height: '70px' }} />
                </div>
                <h2>Admin Access</h2>
                <p>Verify credentials to enter the secure zone.</p>
              </header>

              <form onSubmit={handleLogin} className="login-form">
                <div className="input-field-group">
                  <label>Admin Email</label>
                  <div className="input-wrapper">
                    <Mail size={18} className="input-icon" />
                    <input 
                      type="email" 
                      placeholder="e.g. admin@agency.gov.ng" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required 
                    />
                  </div>
                </div>

                <div className="input-field-group">
                  <label>Secure Password</label>
                  <div className="input-wrapper">
                    <Lock size={18} className="input-icon" />
                    <input 
                      type="password" 
                      placeholder="••••••••••••" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required 
                    />
                  </div>
                </div>

                {/* 🛡️ INLINE ERROR BOX */}
                {error && (
                  <div className="error-status-box">
                    <AlertTriangle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" disabled={loading} className="auth-btn">
                  {loading ? <Loader2 className="spinner" /> : (
                    <>
                      <span>INITIATE AUTHENTICATION</span>
                      <ExternalLink size={18} />
                    </>
                  )}
                </button>
              </form>

              <div className="terms-notice">
  <strong>Terms of Use:</strong> Usage of this system must be approved and 
  assigned by the managing agency, <strong>Nathconsult LLC</strong>. 
  Unauthorized access attempts are subject to investigation.
</div>

{/* 🛡️ NEW LOGIN FOOTER */}
<div className="login-corporate-footer">
  <div className="footer-divider"></div>
  <p>Official Portal of <strong>Nathconsult LLC</strong></p>
  <small>D-U-N-S: 14-188-2601 | Registered in Texas, US</small>
</div>
            </div>
          </div>

          {/* Your existing footer */}
<footer className="login-footer">
  <p>© 2026 <strong>SafeGuard NG</strong>. Managed by <strong>Nathconsult LLC</strong></p>
  
  {/* Add this small link right below or beside it */}
  <div style={{ marginTop: '5px' }}>
    <a 
      href="/privacy" 
      style={{ fontSize: '11px', color: '#fbbf24', textDecoration: 'underline', opacity: 0.8 }}
    >
      Privacy Policy & Data Safety
    </a>
  </div>
</footer>
          
        </div>
      </div>

      <style>{`
        .admin-page-wrapper {
          height: 100vh; width: 100vw; display: flex; overflow: hidden;
          background: #0f172a; font-family: 'Inter', sans-serif; position: relative;
        }

        /* 🏛️ HEADER */
        .login-top-nav {
          position: absolute; top: 0; left: 0; width: 100%; height: 85px;
          background: linear-gradient(90deg, #064e3b 0%, #2e1065 100%);
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 40px; z-index: 100; border-bottom: 2px solid #fbbf24;
        }
        .nav-brand-area { display: flex; align-items: center; gap: 15px; flex: 1; }
        .master-logo { height: 72px; width: auto; }
        .nav-tagline-center { flex: 2; text-align: center; color: rgba(255,255,255,0.7); font-size: 11px; font-weight: 700; letter-spacing: 3px; }
        .nav-spacer { flex: 1; }

        .login-grid { display: grid; grid-template-columns: 1.2fr 1fr; width: 100%; height: 100%; padding-top: 85px; }

        .grid-left { position: relative; display: flex; align-items: center; padding: 80px; overflow: hidden; background: #000; }
        .animated-bg { position: absolute; inset: 0; background-size: cover; background-position: center; animation: cinematicPulse 14s infinite ease-in-out; z-index: 1; }
        .overlay-content { position: relative; z-index: 2; max-width: 550px; animation: longStayFade 14s infinite ease-in-out; }
        
        .hero-text { font-size: 3.5rem; font-weight: 900; line-height: 1.1; margin-bottom: 25px; color: white; }
        .gold-text { color: #fbbf24; }
        .light-text { font-weight: 300; opacity: 0.9; }
        
        .badge { 
          background: rgba(251, 191, 36, 0.15); color: #fbbf24; padding: 8px 16px; border-radius: 50px; 
          font-size: 12px; font-weight: 800; border: 1px solid rgba(251, 191, 36, 0.4); 
          margin-bottom: 25px; display: inline-flex; align-items: center; gap: 8px;
        }

        .description { font-size: 1.15rem; color: #cbd5e1; line-height: 1.7; margin-bottom: 45px; }
        .highlight-white { color: white; font-weight: 600; }
        .highlight-gold { color: #fbbf24; font-weight: 600; }

        .support-box { display: flex; gap: 18px; background: rgba(0,0,0,0.4); padding: 25px; border-radius: 15px; border-left: 5px solid #fbbf24; backdrop-filter: blur(10px); }
        .info-icon-gold { color: #fbbf24; margin-top: 3px; }
        .support-label { font-size: 13px; color: #94a3b8; margin: 0 0 5px 0; }
        .support-link { color: white; text-decoration: none; font-weight: 700; font-size: 16px; transition: 0.3s; }
        .support-link:hover { color: #fbbf24; }

        .grid-right { background: #f8fafc; position: relative; display: flex; flex-direction: column; height: 100%; overflow: hidden; }
        .form-scroll-container { flex: 1; overflow-y: auto; padding: 40px 20px; display: flex; justify-content: center; align-items: flex-start; }
        .form-content-box { width: 100%; max-width: 420px; padding-bottom: 80px; }

        .form-header { text-align: center; margin-bottom: 40px; }
        .form-header h2 { font-size: 2.2rem; color: #0f172a; font-weight: 800; }
        .form-header p { color: #64748b; font-size: 16px; margin-top: 10px; }

        .input-field-group { margin-bottom: 25px; }
        .input-field-group label { display: block; font-size: 14px; font-weight: 800; color: #334155; margin-bottom: 10px; }
        .input-wrapper { display: flex; align-items: center; background: white; border: 2px solid #e2e8f0; border-radius: 14px; padding: 0 18px; height: 58px; transition: 0.3s; }
        .input-wrapper:focus-within { border-color: #064e3b; box-shadow: 0 0 0 5px rgba(6, 78, 59, 0.1); }
        .input-icon { color: #94a3b8; margin-right: 15px; }
        .input-wrapper input { border: none; outline: none; width: 100%; font-size: 16px; color: #0f172a; background: transparent; }



    /*Policy*/
    .login-footer-links {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
}
.policy-link {
  color: #064e3b;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}
          



        /* 🆕 ERROR BOX STYLE */
        .error-status-box {
          background: #fef2f2;
          border: 1px solid #fee2e2;
          color: #b91c1c;
          padding: 14px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          animation: shake 0.4s ease-in-out;
        }



        /*Footer*/
        .login-corporate-footer {
  margin-top: 30px;
  text-align: center;
  color: #64748b; /* Subtle grey */
}

.footer-divider {
  width: 50px;
  height: 2px;
  background: #fbbf24; /* Your gold brand color */
  margin: 0 auto 15px;
  border-radius: 2px;
}

.login-corporate-footer p {
  font-size: 14px;
  margin-bottom: 5px;
  color: #1e293b;
}

.login-corporate-footer small {
  font-size: 11px;
  letter-spacing: 0.5px;
  font-family: 'Courier New', monospace;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
}

        .auth-btn { 
          width: 100%; background: #064e3b; color: white; padding: 20px; border-radius: 14px; border: none; 
          font-weight: 800; font-size: 16px; display: flex; align-items: center; justify-content: center; gap: 12px; cursor: pointer; transition: 0.3s; 
        }
        .auth-btn:hover { background: #053e2f; transform: translateY(-3px); box-shadow: 0 12px 24px -6px rgba(6, 78, 59, 0.4); }

        .terms-notice { margin-top: 40px; font-size: 13px; color: #475569; text-align: center; padding: 20px; background: #f1f5f9; border-radius: 14px; line-height: 1.6; border: 1px solid #e2e8f0; }
        .login-footer { position: absolute; bottom: 0; left: 0; width: 100%; padding: 25px; background: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 13px; color: #94a3b8; z-index: 10; }

        @keyframes cinematicPulse { 0%, 100% { filter: brightness(0.2); transform: scale(1); } 50% { filter: brightness(0.65); transform: scale(1.08); } }
        @keyframes longStayFade { 0%, 75% { opacity: 1; transform: translateY(0); } 85%, 95% { opacity: 0; transform: translateY(15px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .spinner { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 900px) {
          .login-grid { grid-template-columns: 1fr; }
          .grid-left { display: none; }
          .nav-tagline-center { display: none; }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;